import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import { videos } from "@jambushrushti/data/gurudevData";
import { useGalleryFromDB } from "@jambushrushti/hooks/useContent";
import { Button } from "@jambushrushti/components/ui/button";
import { Dialog, DialogContent } from "@jambushrushti/components/ui/dialog";
import {
  X,
  Image,
  Video,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Camera,
  Film,
  ArrowRight,
  Grid3X3,
  LayoutGrid,
  User,
  Crown,
  Building2,
  Building,
} from "lucide-react";
import { Link } from "react-router-dom";

const GALLERY_DIVISIONS = [
  { value: "gurudev", label: "Gurudev Muni Jambuvijayaji", icon: User },
  { value: "legacy", label: "Legacy", icon: Crown },
  { value: "mjrc", label: "MJRC", icon: Building2 },
  { value: "temple", label: "Adani Shantigram Jain Temple", icon: Building },
];

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"photos" | "videos">("photos");
  const [activeDivision, setActiveDivision] = useState<string>("gurudev");
  const [viewMode, setViewMode] = useState<"grid" | "masonry">("grid");

  const {
    data: dbGallery = [],
    isLoading,
    error,
    refetch,
  } = useGalleryFromDB();

  // Filter images by division
  const allImages = dbGallery.map(img => ({
    url: img.image_url,
    thumb: img.image_url,
    alt: img.title,
    category: img.category,
    division: img.category_division || "gurudev",
  }));

  const displayImages = allImages.filter(
    img => img.division === activeDivision
  );

  const openLightbox = (index: number) => {
    // Find the actual index in the filtered array
    const filteredIndex = displayImages.indexOf(displayImages[index]);
    setSelectedImage(filteredIndex);
  };

  const closeLightbox = () => setSelectedImage(null);

  const goToPrevious = () => {
    if (selectedImage !== null) {
      setSelectedImage(
        selectedImage === 0 ? displayImages.length - 1 : selectedImage - 1
      );
    }
  };

  const goToNext = () => {
    if (selectedImage !== null) {
      setSelectedImage(
        selectedImage === displayImages.length - 1 ? 0 : selectedImage + 1
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (selectedImage === null) {
      return;
    }
    if (e.key === "ArrowLeft") {
      goToPrevious();
    }
    if (e.key === "ArrowRight") {
      goToNext();
    }
    if (e.key === "Escape") {
      closeLightbox();
    }
  };

  const getDivisionCount = (division: string) => {
    return allImages.filter(img => img.division === division).length;
  };

  return (
    <Layout>
      <Seo
        title="Photo & Video Gallery"
        description="Explore rare photographs and videos of Gurudev Muni Jambuvijayji Maharaj Saheb, capturing moments from his life of scholarship and spiritual dedication."
      />
      {/* Hero Section */}
      <section
        className="relative overflow-hidden py-6 sm:py-10 lg:py-20"
        style={{ backgroundColor: "#FAF7F2" }}
      >
        <div className="container relative mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <div
              className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium sm:mb-6 sm:px-4 sm:py-2 sm:text-sm"
              style={{
                backgroundColor: "white",
                border: "1px solid #E8E4DD",
                color: "#D2811D",
              }}
            >
              <Camera className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Visual Archive
            </div>

            <h1
              className="mb-3 text-3xl font-bold sm:mb-6 sm:text-5xl lg:text-6xl"
              style={{
                fontFamily: "Playfair Display, serif",
                color: "#2D2A26",
              }}
            >
              Photo & Video Gallery
            </h1>

            <p
              className="mx-auto mb-4 max-w-2xl text-base sm:mb-8 sm:text-lg"
              style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
            >
              Explore rare photographs and videos of Gurudev Muni Jambuvijayji
              Maharaj Saheb, capturing moments from his life of scholarship and
              spiritual dedication.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button
                onClick={() => setActiveTab("photos")}
                style={
                  activeTab === "photos"
                    ? { backgroundColor: "#D2811D", color: "white" }
                    : {
                        backgroundColor: "white",
                        color: "#2D2A26",
                        border: "1px solid #E8E4DD",
                      }
                }
              >
                <Image className="mr-2 h-4 w-4" />
                View Photos
              </Button>
              <Button
                onClick={() => setActiveTab("videos")}
                style={
                  activeTab === "videos"
                    ? { backgroundColor: "#D2811D", color: "white" }
                    : {
                        backgroundColor: "white",
                        color: "#2D2A26",
                        border: "1px solid #E8E4DD",
                      }
                }
              >
                <Video className="mr-2 h-4 w-4" />
                Watch Videos
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Division Tabs */}
      {activeTab === "photos" && (
        <section
          className="py-6"
          style={{
            backgroundColor: "white",
            borderBottom: "1px solid #E8E4DD",
          }}
        >
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap items-center justify-center gap-3">
              {GALLERY_DIVISIONS.map(division => {
                const Icon = division.icon;
                const count = getDivisionCount(division.value);
                return (
                  <Button
                    key={division.value}
                    onClick={() => setActiveDivision(division.value)}
                    className="gap-2"
                    style={
                      activeDivision === division.value
                        ? { backgroundColor: "#D2811D", color: "white" }
                        : {
                            backgroundColor: "white",
                            color: "#2D2A26",
                            border: "1px solid #E8E4DD",
                          }
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {division.label}
                    <span
                      className="ml-1 rounded-full px-2 py-0.5 text-xs"
                      style={{
                        backgroundColor:
                          activeDivision === division.value
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(210,129,29,0.1)",
                        color:
                          activeDivision === division.value
                            ? "white"
                            : "#D2811D",
                      }}
                    >
                      {count}
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Feature Highlights */}
      <section
        className="py-6 sm:py-10"
        style={{ backgroundColor: "white", borderBottom: "1px solid #E8E4DD" }}
      >
        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-3">
            <div className="flex items-start gap-4">
              <div
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: "rgba(210,129,29,0.1)" }}
              >
                <Camera className="h-6 w-6" style={{ color: "#D2811D" }} />
              </div>
              <div>
                <h3
                  className="mb-1 font-semibold"
                  style={{
                    fontFamily: "Playfair Display, serif",
                    color: "#2D2A26",
                  }}
                >
                  Rare Photographs
                </h3>
                <p
                  className="text-sm"
                  style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
                >
                  {allImages.length}+ photos from Gurudev's life and teachings
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: "rgba(210,129,29,0.1)" }}
              >
                <Film className="h-6 w-6" style={{ color: "#D2811D" }} />
              </div>
              <div>
                <h3
                  className="mb-1 font-semibold"
                  style={{
                    fontFamily: "Playfair Display, serif",
                    color: "#2D2A26",
                  }}
                >
                  Video Collection
                </h3>
                <p
                  className="text-sm"
                  style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
                >
                  {videos.length}+ videos of discourses and events
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: "rgba(210,129,29,0.1)" }}
              >
                <LayoutGrid className="h-6 w-6" style={{ color: "#D2811D" }} />
              </div>
              <div>
                <h3
                  className="mb-1 font-semibold"
                  style={{
                    fontFamily: "Playfair Display, serif",
                    color: "#2D2A26",
                  }}
                >
                  High Quality
                </h3>
                <p
                  className="text-sm"
                  style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
                >
                  View in full resolution with lightbox
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      {activeTab === "photos" && (
        <section
          className="py-16 lg:py-20"
          style={{ backgroundColor: "#FAF7F2" }}
        >
          <div className="container mx-auto px-4">
            <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <div>
                <h2
                  className="mb-1 text-2xl font-bold"
                  style={{
                    fontFamily: "Playfair Display, serif",
                    color: "#2D2A26",
                  }}
                >
                  {GALLERY_DIVISIONS.find(d => d.value === activeDivision)
                    ?.label || "Photo Collection"}
                </h2>
                <p
                  style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
                >
                  Showing {displayImages.length} photos from the{" "}
                  {GALLERY_DIVISIONS.find(
                    d => d.value === activeDivision
                  )?.label.toLowerCase()}{" "}
                  collection
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  style={
                    viewMode === "grid"
                      ? {
                          backgroundColor: "#D2811D",
                          color: "white",
                          border: "1px solid #D2811D",
                        }
                      : {
                          backgroundColor: "white",
                          border: "1px solid #E8E4DD",
                          color: "#2D2A26",
                        }
                  }
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode("masonry")}
                  style={
                    viewMode === "masonry"
                      ? {
                          backgroundColor: "#D2811D",
                          color: "white",
                          border: "1px solid #D2811D",
                        }
                      : {
                          backgroundColor: "white",
                          border: "1px solid #E8E4DD",
                          color: "#2D2A26",
                        }
                  }
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2
                  className="h-10 w-10 animate-spin"
                  style={{ color: "#D2811D" }}
                />
              </div>
            ) : error ? (
              <div role="alert" className="space-y-4 py-20 text-center">
                <p className="text-destructive">{error.message}</p>
                <Button variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            ) : displayImages.length === 0 ? (
              <div
                className="rounded-2xl py-20 text-center"
                style={{
                  backgroundColor: "white",
                  border: "1px solid #E8E4DD",
                }}
              >
                <Camera
                  className="mx-auto mb-4 h-16 w-16"
                  style={{ color: "#6B6764" }}
                />
                <h3
                  className="mb-2 text-xl font-semibold"
                  style={{
                    fontFamily: "Playfair Display, serif",
                    color: "#2D2A26",
                  }}
                >
                  No Photos Available
                </h3>
                <p
                  style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
                >
                  Photos for this category will be added soon.
                </p>
              </div>
            ) : (
              <div
                className={
                  viewMode === "grid"
                    ? "grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
                    : "columns-2 gap-4 space-y-4 sm:columns-3 md:columns-4 lg:columns-5"
                }
              >
                {displayImages.map((image, index) => (
                  <div
                    key={index}
                    className={` ${viewMode === "grid" ? "aspect-square" : "mb-4 break-inside-avoid"} group relative cursor-pointer overflow-hidden rounded-xl shadow-sm transition-all duration-300 hover:shadow-xl`}
                    style={{ backgroundColor: "white" }}
                    onClick={() => openLightbox(index)}
                  >
                    <img
                      src={image.thumb || image.url}
                      alt={image.alt || `Photo ${index + 1}`}
                      className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${viewMode === "grid" ? "h-full" : "h-auto"} `}
                      onError={e => {
                        (e.target as HTMLImageElement).src = "/placeholder.svg";
                      }}
                    />
                    <div
                      className="absolute inset-0 flex items-end justify-center pb-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(45,42,38,0.8), transparent)",
                      }}
                    >
                      <span
                        className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-white"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          backdropFilter: "blur(4px)",
                        }}
                      >
                        <Image className="h-4 w-4" />
                        View Photo
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Video Gallery */}
      {activeTab === "videos" && (
        <section
          className="py-16 lg:py-20"
          style={{ backgroundColor: "#FAF7F2" }}
        >
          <div className="container mx-auto px-4">
            <div className="mb-8">
              <h2
                className="mb-1 text-2xl font-bold"
                style={{
                  fontFamily: "Playfair Display, serif",
                  color: "#2D2A26",
                }}
              >
                Video Collection
              </h2>
              <p style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}>
                {videos.length} videos from official channels
              </p>
            </div>

            <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2 lg:grid-cols-3">
              {videos.length === 0 ? (
                <p className="col-span-full py-8 text-center text-muted-foreground">
                  No records available.
                </p>
              ) : (
                videos.map((video, index) => (
                  <div
                    key={video.id}
                    className="group overflow-hidden rounded-2xl shadow-sm transition-all duration-300 hover:shadow-xl"
                    style={{ backgroundColor: "white" }}
                  >
                    <div
                      className="relative aspect-video"
                      style={{ backgroundColor: "#2D2A26" }}
                    >
                      <iframe
                        src={`https://www.youtube.com/embed/${video.url.split("v=")[1]}`}
                        title={video.title}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <div className="p-5">
                      <h3
                        className="mb-2 line-clamp-2 font-semibold transition-colors group-hover:opacity-80"
                        style={{
                          fontFamily: "Playfair Display, serif",
                          color: "#2D2A26",
                        }}
                      >
                        {video.title}
                      </h3>
                      <p
                        className="mb-3 text-sm"
                        style={{
                          fontFamily: "Inter, sans-serif",
                          color: "#6B6764",
                        }}
                      >
                        {video.channel}
                      </p>
                      {video.description && (
                        <p
                          className="mb-4 line-clamp-2 text-sm"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            color: "#6B6764",
                          }}
                        >
                          {video.description}
                        </p>
                      )}
                      <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
                        style={{ color: "#D2811D" }}
                      >
                        Watch on YouTube
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div
              className="mt-12 rounded-2xl p-8 text-center"
              style={{ backgroundColor: "white", border: "1px solid #E8E4DD" }}
            >
              <p
                className="mb-4"
                style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
              >
                More videos available on the official YouTube channel
              </p>
              <Button
                asChild
                style={{ backgroundColor: "#D2811D", color: "white" }}
              >
                <a
                  href="https://www.youtube.com/@JambuParivar"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit YouTube Channel
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Source Attribution */}
      <section
        className="py-6"
        style={{ backgroundColor: "white", borderTop: "1px solid #E8E4DD" }}
      >
        <div className="container mx-auto px-4 text-center">
          <p
            className="text-sm"
            style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
          >
            All photos and videos are sourced from{" "}
            <a
              href="https://siddhijambuparivar.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
              style={{ color: "#D2811D" }}
            >
              siddhijambuparivar.com
            </a>{" "}
            and official YouTube channels.
          </p>
        </div>
      </section>

      {/* Lightbox */}
      <Dialog
        open={selectedImage !== null}
        onOpenChange={() => closeLightbox()}
      >
        <DialogContent
          className="max-w-6xl border-none p-0 backdrop-blur-xl"
          style={{ backgroundColor: "rgba(45,42,38,0.98)" }}
          onKeyDown={handleKeyDown}
        >
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 z-10 text-white"
              style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              onClick={closeLightbox}
            >
              <X className="h-5 w-5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 text-white"
              style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              onClick={goToPrevious}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 text-white"
              style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              onClick={goToNext}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>

            {selectedImage !== null && displayImages[selectedImage] && (
              <div className="flex min-h-[60vh] items-center justify-center p-8">
                <img
                  src={displayImages[selectedImage].url}
                  alt={
                    displayImages[selectedImage].alt ||
                    `Photo ${selectedImage + 1}`
                  }
                  className="max-h-[80vh] max-w-full rounded-lg object-contain"
                  onError={e => {
                    (e.target as HTMLImageElement).src = "/placeholder.svg";
                  }}
                />
              </div>
            )}

            {selectedImage !== null && displayImages[selectedImage] && (
              <div className="pb-6 text-center">
                <p
                  className="mb-2 text-sm"
                  style={{ color: "rgba(255,255,255,0.6)" }}
                >
                  {selectedImage + 1} / {displayImages.length}
                </p>
                {displayImages[selectedImage].alt && (
                  <p className="mx-auto max-w-xl px-4 text-white">
                    {displayImages[selectedImage].alt}
                  </p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section
        className="py-16 lg:py-20"
        style={{ backgroundColor: "#2D2A26" }}
      >
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2
              className="mb-4 text-2xl font-bold text-white md:text-3xl"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Explore More of Gurudev's Legacy
            </h2>
            <p
              className="mb-8 text-white/70"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Discover the vast collection of Gurudev's teachings, books, and
              spiritual wisdom.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                style={{ backgroundColor: "#D2811D", color: "white" }}
              >
                <Link to="/about/gurudev">
                  About Gurudev
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white bg-transparent text-white transition-colors hover:bg-white hover:text-foreground"
              >
                <Link to="/books">View Books Collection</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white bg-transparent text-white transition-colors hover:bg-white hover:text-foreground"
              >
                <Link to="/live-telecast">Live Telecasts</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Gallery;
